import type {
  UpdateLedgerAccountPayload,
  UpdateLedgerAccountResponse,
} from './types';

import { ENDPOINT } from '~/constants/endpoints';

import type { UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import {
  zodUpdateLedgerAccountPayloadSchema,
  zodUpdateLedgerAccountResponseSchema,
} from './validations';

/**
 * Opens a new effective-dated version of a ledger account: the API closes
 * the currently open version and creates the new one in a single call. The
 * response keeps the same account `id` but carries a freshly generated
 * `versionId`, which callers never choose or send. Responds `409` when the
 * new Firma + Kontonummer + Gültig-von combination collides with an
 * existing version.
 *
 * @param ledgerAccountId - The ledger account's own id, stable across all of its versions.
 * @param payload - The new version's fields.
 * @returns The newly created ledger account version.
 * @throws VestigasHTTPError if the API request fails or validation fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Ledger-Accounts/operation/update_ledger_account_ledger_accounts__ledger_account_id__patch
 */
export const updateLedgerAccount = async (
  ledgerAccountId: UUID,
  payload: UpdateLedgerAccountPayload,
) =>
  vestigasApi
    .patch(ENDPOINT.LEDGER_ACCOUNT.UPDATE(ledgerAccountId), {
      json: payload,
      validate: {
        body: zodUpdateLedgerAccountPayloadSchema,
        response: zodUpdateLedgerAccountResponseSchema,
      },
    })
    .json<UpdateLedgerAccountResponse>();
